import { Helmet } from 'react-helmet'
import MainHeader from '../../components/MainHeader'
import MainFooter from '../../components/MainFooter'
import CTAButton from '../../components/CTAButton'
import { motion } from "framer-motion";

import './Support.css'
import './MSupport.css'
import { boxvar2_1, oPU, getToniSummary, getToniContact, TONI_MAIL_MESSAGE_MAX_CHARS } from '../../core.js'
import { faCheck, faCopy, faEnvelope, faPhone, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const HUBSPOT_FORM_ID = 'e5bb269a-5421-491b-9154-59c8873f9281'
const HUBSPOT_PORTAL_ID = '146312247'
const HUBSPOT_V2_SCRIPT = 'https://js-eu1.hsforms.net/forms/v2.js'

const MESSAGE_FIELD_PATTERN = /message|nachricht|anliegen|content|note|beschreibung|comment|kommentar/i

const PAKET_LABELS = {
    'web-starter': 'Webentwicklung Starter (ab 990 €)',
    'web-business': 'Webentwicklung Business (ab 2.190 €)',
    'web-plattform': 'Webentwicklung Plattform (ab 6.900 €)',
    'care': 'Care Hosting (39 €/Monat)',
    'care-plus': 'Care Plus (99 €/Monat)',
    'care-pro': 'Care Pro (199 €/Monat)',
    'foto-basic': 'Fotografie Shooting Basic (ab 190 €)',
    'foto-event': 'Fotografie Event-Paket (ab 590 €)',
    'foto-business': 'Fotografie Business-Tag (ab 990 €)',
    'content-starter': 'Content Starter DIY (290 €/Monat)',
    'content-wachstum': 'Content Wachstum (590 €/Monat)',
    'content-vollgas': 'Content Vollgas (1.490 €/Monat)',
    'branding': 'Branding / Logo (ab 490 €)',
    'sonderloesung': 'Individuelles Projekt',
    'bundle-verein': 'Bundle Verein-Komplett (ab 1.290 € + 29 €/Monat)',
    'bundle-kmu': 'Bundle KMU-Wachstum (ab 2.490 € + 99 €/Monat)',
    'bundle-event': 'Bundle Event-Boost (ab 1.490 €)',
}

function buildPaketMessage(paketSlug) {
    const label = PAKET_LABELS[paketSlug] || paketSlug.replace(/-/g, ' ')
    return `Hallo ToNetwork,\n\nich interessiere mich für folgendes Angebot:\n${label}\n\nBitte meldet euch mit einem unverbindlichen Angebot.`
}

function buildPrefillPayload() {
    const params = new URLSearchParams(window.location.search)
    const paketSlug = params.get('paket')
    const fromToni = params.get('from') === 'toni'
    const toniSummary = fromToni ? getToniSummary() : null
    const toniContact = fromToni ? getToniContact() : null

    const parts = []
    if (paketSlug) parts.push(buildPaketMessage(paketSlug))
    if (toniSummary) parts.push(toniSummary)

    const hasContact = !!(toniContact && (toniContact.email || toniContact.name))

    return {
        message: parts.join('\n\n---\n\n'),
        paketSlug,
        paketLabel: paketSlug ? (PAKET_LABELS[paketSlug] || paketSlug) : null,
        contact: hasContact ? toniContact : null,
        hasContact,
        hasToni: !!toniSummary,
        hasPaket: !!paketSlug,
        hasAnything: parts.length > 0 || hasContact,
    }
}

/** HubSpot liest beim Formularstart passende Query-Parameter (Feldname = Param-Name). */
function injectHubspotUrlParams(payload) {
    if (!payload?.hasAnything) return

    const url = new URL(window.location.href)
    const msg = payload.message

    // Kurze Texte vollständig, lange gekürzt (URL-Längenlimit).
    if (msg.length <= TONI_MAIL_MESSAGE_MAX_CHARS) {
        url.searchParams.set('message', msg)
    } else if (!url.searchParams.has('message')) {
        url.searchParams.set('message', msg.slice(0, TONI_MAIL_MESSAGE_MAX_CHARS) + '…')
    }

    if (payload.paketSlug) url.searchParams.set('paket', payload.paketSlug)
    if (payload.paketLabel) url.searchParams.set('interesse', payload.paketLabel)

    // HubSpot befüllt Standard-Kontaktfelder automatisch aus passenden Query-Parametern.
    const c = payload.contact
    if (c) {
        if (c.firstname) url.searchParams.set('firstname', c.firstname)
        if (c.lastname) url.searchParams.set('lastname', c.lastname)
        if (c.email) url.searchParams.set('email', c.email)
        if (c.phone) url.searchParams.set('phone', c.phone)
    }

    window.history.replaceState({}, '', url.toString())
}

function flattenFieldKeys(obj, prefix = '') {
    const keys = []
    if (!obj || typeof obj !== 'object') return keys
    for (const [k, v] of Object.entries(obj)) {
        const path = prefix ? `${prefix}/${k}` : k
        keys.push(path)
        keys.push(k)
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            keys.push(...flattenFieldKeys(v, path))
        }
    }
    return [...new Set(keys)]
}

async function readFieldValue(form, fieldName) {
    if (typeof form.getFieldValue === 'function') {
        try {
            const v = await form.getFieldValue(fieldName)
            if (v != null) return v
        } catch (e) { /* */ }
    }
    if (typeof form.getFormFieldValues === 'function') {
        const vals = await form.getFormFieldValues()
        return vals?.[fieldName]
    }
    return undefined
}

async function setFieldValueAsync(form, fieldName, value) {
    if (typeof form.setFieldValue !== 'function') return
    await Promise.resolve(form.setFieldValue(fieldName, value))
}

async function verifyTextInForm(form, text) {
    const snippet = text.slice(0, 20)
    if (typeof form.getFormFieldValues !== 'function') return false

    const vals = await form.getFormFieldValues()
    const flat = flattenFieldKeys(vals)
    for (const key of flat) {
        const v = vals?.[key] ?? await readFieldValue(form, key)
        if (typeof v === 'string' && v.includes(snippet)) return true
    }
    for (const v of Object.values(vals || {})) {
        if (typeof v === 'string' && v.includes(snippet)) return true
    }
    return false
}

async function prefillViaV4(form, text) {
    if (!form || !text) return false

    let keys = []
    if (typeof form.getFormFieldValues === 'function') {
        keys = flattenFieldKeys(await form.getFormFieldValues())
    }

    const ordered = [
        ...keys.filter((k) => MESSAGE_FIELD_PATTERN.test(k)),
        ...keys.filter((k) => !MESSAGE_FIELD_PATTERN.test(k)),
        '0-1/message', '0-2/message', '0-3/message', 'message', 'nachricht',
    ]

    for (const name of [...new Set(ordered)]) {
        try {
            await setFieldValueAsync(form, name, text)
            await new Promise((r) => setTimeout(r, 200))
            if (await verifyTextInForm(form, text)) return true
        } catch (e) { /* next */ }
    }
    return false
}

function prefillViaLegacyJquery($form, text) {
    if (!text || !$form?.find) return false
    const snippet = text.slice(0, 20)

    const $areas = $form.find('textarea')
    if ($areas.length) {
        $areas.val(text).trigger('change')
        if (($areas.val() || '').includes(snippet)) return true
    }

    const $inputs = $form.find('input[type="text"]')
    if ($inputs.length) {
        $inputs.first().val(text).trigger('change')
        if (($inputs.first().val() || '').includes(snippet)) return true
    }
    return false
}

function prefillViaIframeDom(root, text) {
    if (!text || !root) return false
    const snippet = text.slice(0, 20)
    const iframe = root.querySelector('iframe')

    if (iframe) {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document
            const ta = doc?.querySelector('textarea')
            if (ta) {
                ta.value = text
                ta.dispatchEvent(new Event('input', { bubbles: true }))
                ta.dispatchEvent(new Event('change', { bubbles: true }))
                if (ta.value.includes(snippet)) return true
            }
        } catch (e) { /* cross-origin */ }
    }

    const ta = root.querySelector('textarea')
    if (ta) {
        ta.value = text
        ta.dispatchEvent(new Event('input', { bubbles: true }))
        ta.dispatchEvent(new Event('change', { bubbles: true }))
        if (ta.value.includes(snippet)) return true
    }
    return false
}

async function prefillContactViaV4(form, contact) {
    if (!form || !contact || typeof form.setFieldValue !== 'function') return

    let keys = []
    if (typeof form.getFormFieldValues === 'function') {
        try { keys = flattenFieldKeys(await form.getFormFieldValues()) } catch (e) { /* */ }
    }

    const targets = [
        { re: /first\s*-?name|vorname/i, value: contact.firstname, fallbacks: ['firstname', '0-1/firstname'] },
        { re: /last\s*-?name|nachname/i, value: contact.lastname || contact.name, fallbacks: ['lastname', '0-1/lastname'] },
        { re: /e-?mail/i, value: contact.email, fallbacks: ['email', '0-1/email'] },
        { re: /phone|telefon|mobile|^tel$/i, value: contact.phone, fallbacks: ['phone', 'mobilephone', '0-1/phone'] },
    ]

    for (const t of targets) {
        if (!t.value) continue
        const matched = keys.filter((k) => t.re.test(k))
        const candidates = [...new Set([...matched, ...t.fallbacks])]
        for (const name of candidates) {
            try { await setFieldValueAsync(form, name, t.value) } catch (e) { /* next */ }
        }
    }
}

function getV4Form(event) {
    if (window.HubSpotFormsV4?.getFormFromEvent) {
        const f = window.HubSpotFormsV4.getFormFromEvent(event)
        if (f) return f
    }
    const all = window.HubSpotFormsV4?.getForms?.()
    return all?.[0] || null
}

async function runAllPrefillMethods(payload, form, $form, container) {
    // Kontaktfelder (Name, E-Mail, Telefon) best-effort über die Form-API setzen.
    if (form && payload?.contact) {
        try { await prefillContactViaV4(form, payload.contact) } catch (e) { /* */ }
    }

    if (!payload?.message) return !!payload?.hasContact
    const text = payload.message

    if (form && await prefillViaV4(form, text)) return true
    if ($form && prefillViaLegacyJquery($form, text)) return true
    if (container && prefillViaIframeDom(container, text)) return true

    // V4 ohne Verifikation als letzter Versuch (iframe zeigt Wert, API-Leser schweigt).
    if (form) {
        const keys = typeof form.getFormFieldValues === 'function'
            ? flattenFieldKeys(await form.getFormFieldValues())
            : []
        const target = keys.find((k) => MESSAGE_FIELD_PATTERN.test(k)) || 'message'
        try {
            await setFieldValueAsync(form, target, text)
            return true
        } catch (e) { /* */ }
    }
    return false
}

export default function Support() {
    const [prefillInfo, setPrefillInfo] = useState(null)
    const [prefillStatus, setPrefillStatus] = useState('idle')
    const [copied, setCopied] = useState(false)

    const payloadRef = useRef(null)
    const prefillOkRef = useRef(false)
    const applyingRef = useRef(false)
    const formContainerRef = useRef(null)
    const legacyFormRef = useRef(null)

    useEffect(() => {
        const payload = buildPrefillPayload()
        payloadRef.current = payload

        if (payload.hasAnything) {
            injectHubspotUrlParams(payload)
            setPrefillInfo(payload)
            setPrefillStatus('pending')
            prefillOkRef.current = false

            // Zwischenablage sofort befüllen – zuverlässiger Fallback bei iframe-Formularen.
            navigator.clipboard?.writeText(payload.message).catch(() => { })
        }

        let failTimer = null

        async function attemptPrefill(form, $form) {
            if (!payloadRef.current?.hasAnything || applyingRef.current) return
            if (prefillOkRef.current) return

            applyingRef.current = true
            let ok = false

            for (let i = 0; i < 6; i++) {
                if (i > 0) await new Promise((r) => setTimeout(r, 400 * i))
                ok = await runAllPrefillMethods(
                    payloadRef.current,
                    form,
                    $form || legacyFormRef.current,
                    formContainerRef.current,
                )
                if (ok) break
            }

            applyingRef.current = false

            if (ok) {
                prefillOkRef.current = true
                setPrefillStatus('ok')
                if (failTimer) clearTimeout(failTimer)
            }
        }

        async function onV4Ready(event) {
            const form = getV4Form(event)
            if (form) await attemptPrefill(form, legacyFormRef.current)
        }

        function onLegacyMessage(event) {
            if (event.data?.type !== 'hsFormCallback') return
            if (event.data?.eventName !== 'onFormReady') return
            if (event.data?.id && event.data.id !== HUBSPOT_FORM_ID) return
            attemptPrefill(getV4Form(event), legacyFormRef.current)
        }

        window.addEventListener('hs-form-event:on-ready', onV4Ready)
        window.addEventListener('hs-form-event:on-interaction:navigate', onV4Ready)
        window.addEventListener('message', onLegacyMessage)

        if (payload.hasAnything) {
            failTimer = setTimeout(() => {
                if (!prefillOkRef.current) setPrefillStatus('clipboard')
            }, 12000)
        }

        function initHubspotForm() {
            if (!window.hbspt?.forms?.create) return

            window.hbspt.forms.create({
                region: 'eu1',
                portalId: HUBSPOT_PORTAL_ID,
                formId: HUBSPOT_FORM_ID,
                target: '#hubspot-form-target',
                onFormReady: function ($form) {
                    legacyFormRef.current = $form
                    const v4 = window.HubSpotFormsV4?.getForms?.()?.[0]
                    attemptPrefill(v4, $form)
                },
            })
        }

        if (window.hbspt?.forms?.create) {
            initHubspotForm()
        } else {
            const script = document.createElement('script')
            script.src = HUBSPOT_V2_SCRIPT
            script.async = true
            script.onload = initHubspotForm
            document.body.appendChild(script)
        }

        return () => {
            if (failTimer) clearTimeout(failTimer)
            window.removeEventListener('hs-form-event:on-ready', onV4Ready)
            window.removeEventListener('hs-form-event:on-interaction:navigate', onV4Ready)
            window.removeEventListener('message', onLegacyMessage)
        }
    }, []);

    function copyPrefillText() {
        const text = prefillInfo?.message
        if (!text) return
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    function prefillHintText() {
        if (prefillStatus === 'pending') {
            return 'Deine Angaben werden ins Formular übernommen …'
        }
        if (prefillStatus === 'ok') {
            if (prefillInfo?.hasContact) {
                return 'Deine Kontaktdaten und deine ToNi-Zusammenfassung wurden vorausgefüllt – bitte prüfe alle Felder und sende das Formular ab.'
            }
            if (prefillInfo?.hasToni && prefillInfo?.hasPaket) {
                return `Paket „${prefillInfo.paketLabel}“ und ToNi-Zusammenfassung wurden vorausgefüllt – bitte prüfe das Nachrichtenfeld.`
            }
            if (prefillInfo?.hasPaket) {
                return `Dein Paket „${prefillInfo.paketLabel}“ wurde vorausgefüllt – bitte ergänze deine Kontaktdaten.`
            }
            return 'Deine ToNi-Zusammenfassung wurde vorausgefüllt – bitte prüfe das Nachrichtenfeld.'
        }
        return 'Der Text liegt in deiner Zwischenablage. Klicke ins Nachrichtenfeld im Formular und füge ein mit Strg+V (Windows) bzw. ⌘V (Mac).'
    }

    const showPrefillPanel = prefillInfo && prefillStatus !== 'idle'
    const showCopyPanel = showPrefillPanel && (prefillStatus === 'clipboard' || prefillStatus === 'failed')

    return (
        <div className='mainCon' id='mainCon'>

            <MainHeader selectedsite={"support"} disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>Kontaktbereich</title>
                <meta property="og:title" content="Support" />
                <meta property="og:description" content="Erreiche uns schneller als die Polizei erlaubt. Willkommen in unserem Kontaktbereich." />
                <meta name="description" content="Erreiche uns schneller als die Polizei erlaubt. Willkommen in unserem Kontaktbereich." />
            </Helmet>
            <section className='sec_next' id='sec_next--support'>
                <div className='sec_next_cont' id="sec_next--support--cont" style={{ wordBreak: "break-word" }}>


                    <motion.div variants={boxvar2_1} className='about_section_cont' initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: "-50px" }}>
                        <br />
                        <h2>Stay in contact.</h2>
                        <p>
                            Wir stehen dir immer zur Seite.<br />
                            Hier erfährst du, wie du uns am besten erreichen kannst.<br />
                            <a href='/chat'>Oder frag ToNi – unseren KI-Chat zu Paketen und Preisen.</a>
                        </p>

                        <div className='contact_cont' id='contactus'>
                            <b>ToNetwork UG (haftungsbeschränkt)</b>
                            <br />
                            <p>
                                Geschäftsführer: David Adrian Heidl<br />
                                87749 Hawangen
                            </p>
                            <a href='mailto:hey@tonetwork.de'><FontAwesomeIcon className='contacticon' icon={faEnvelope} /> hey@tonetwork.de</a>
                            <a href='tel:083324908872'><FontAwesomeIcon className='contacticon' icon={faPhone} />  08332 4908872</a>
                            <a href='https://wa.me/+4915792358626' target='_blank' rel='noopener noreferrer'><FontAwesomeIcon className='contacticon' icon={faWhatsapp} /> 01579 2358626</a>
                            <a href='https://instagram.com/tonetwork.de' target='_blank' rel='noopener noreferrer'><FontAwesomeIcon className='contacticon' icon={faInstagram} /> tonetwork.de</a>

                            <CTAButton className="contact_cta cta-button" showArrow={false} lightcta={true} customIcon={faPhone} iconLeft={true} title={"Kostenlosen Termin vereinbaren"} onclick={() => { oPU('https://calendly.com/tonetworkde-meet/getintouch') }}></CTAButton>

                        </div>

                        <div className='hubspot_cont' id='hubspotform'>
                            <h3>Kontaktformular (Hubspot CRM)</h3>

                            {showPrefillPanel && (
                                <div className={`support_prefill support_prefill-${prefillStatus}`}>
                                    <div className='support_prefill_icon'>
                                        {prefillStatus === 'pending' && <FontAwesomeIcon icon={faSpinner} spin />}
                                        {prefillStatus === 'ok' && <FontAwesomeIcon icon={faCheck} />}
                                        {(prefillStatus === 'clipboard' || prefillStatus === 'failed') && <FontAwesomeIcon icon={faCopy} />}
                                    </div>
                                    <p className='support_prefill_text'>{prefillHintText()}</p>

                                    {(showCopyPanel) && prefillInfo.message && (
                                        <div className='support_prefill_fallback'>
                                            <pre className='support_prefill_preview'>{prefillInfo.message}</pre>
                                            <button type='button' className='support_prefill_copy' onClick={copyPrefillText}>
                                                <FontAwesomeIcon icon={faCopy} />
                                                {copied ? 'Kopiert!' : 'Nochmal kopieren'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div id='hubspot-form-target' className='hubspot_form_target' ref={formContainerRef} />
                        </div>

                    </motion.div>

                </div>
            </section >

            <MainFooter hidecontact={true} />
        </div >
    )
}
